import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Maximize2, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import { loadIngestSourceBlob } from './sourceBlob';
import { resolveOrgId } from '../utils/authHeaders';
import { openPdf, renderPage } from './pdfRender';
import SourceTable, { asTable } from './SourceTable';

export const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];

export function kindOf(contentType) {
  const t = String(contentType || '').toLowerCase();
  if (t.includes('pdf')) return 'pdf';
  if (t.startsWith('image/')) return 'image';
  if (t.includes('json') || t.startsWith('text/')) return 'text';
  return 'other';
}

const btn = 'min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg border border-surface-border bg-surface text-ink hover:bg-surface-sunken disabled:opacity-40';

export default function SourceViewer({ documentId }) {
  const [src, setSrc] = useState({ loading: true });
  const [zoomIdx, setZoomIdx] = useState(2);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [text, setText] = useState('');
  const [table, setTable] = useState(null);
  const [rotate, setRotate] = useState(0);
  const pdfRef = useRef(null);
  const canvasRef = useRef(null);
  const boxRef = useRef(null);
  const zoom = ZOOMS[zoomIdx];

  useEffect(() => {
    let cancelled = false;
    let url = null;
    setSrc({ loading: true });
    setPage(1);
    pdfRef.current = null;
    (async () => {
      const r = await loadIngestSourceBlob(documentId, { orgId: resolveOrgId() });
      url = r.url;
      if (cancelled) return;
      if (r.error) { setSrc({ error: r.error }); return; }
      const kind = kindOf(r.contentType);
      if (kind === 'pdf') {
        try {
          const buf = await (await fetch(r.url)).arrayBuffer();
          const pdf = await openPdf(buf);
          if (cancelled) return;
          pdfRef.current = pdf;
          setPages(pdf.numPages);
        } catch {
          if (!cancelled) setSrc({ url: r.url, kind: 'other' });
          return;
        }
      }
      let shown = kind;
      if (kind === 'text') {
        const t = await (await fetch(r.url)).text().catch(() => '');
        let json = null;
        try { json = JSON.stringify(JSON.parse(t), null, 2); } catch { json = null; }
        const tbl = json === null ? asTable(t) : null;  // CSV invoice: a table, not a wall of text
        setTable(tbl);
        setText(json ?? t);
        if (tbl) shown = 'table';
      }
      if (kind === 'other' && /octet-stream|csv|excel/i.test(r.contentType || '')) {
        // An uploaded CSV is often stored without a text type; if the bytes are a header row plus rows, show the table.
        const t = await (await fetch(r.url)).text().catch(() => '');
        const tbl = t && t.length < 2_000_000 && !t.includes('\u0000') ? asTable(t) : null;
        if (tbl) { setTable(tbl); setText(t); shown = 'table'; }
      }
      if (!cancelled) setSrc({ url: r.url, kind: shown });
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [documentId]);

  useEffect(() => {
    if (src.kind !== 'pdf' || !pdfRef.current || !canvasRef.current) return undefined;
    let cancel = null;
    const width = (boxRef.current?.clientWidth || 600) - 16;
    renderPage(pdfRef.current, page, canvasRef.current, { width, zoom, rotate })
      .then((c) => { cancel = c; }).catch(() => {});
    return () => { if (cancel) cancel(); };
  }, [src, page, zoom, rotate]);

  return (
    <section className="flex flex-col h-full rounded-2xl border border-surface-border bg-surface overflow-hidden" data-testid="source-viewer">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-surface-border">
        <h2 className="text-sm font-medium text-ink mr-auto">Original document</h2>
        {(src.kind === 'pdf' || src.kind === 'image') && (
          <>
            <button type="button" className={btn} aria-label="Zoom out" data-testid="source-zoom-out" disabled={zoomIdx === 0} onClick={() => setZoomIdx((z) => z - 1)}><ZoomOut className="w-4 h-4" /></button>
            <span className="text-xs font-mono text-ink-muted w-12 text-center" data-testid="source-zoom-level">{Math.round(zoom * 100)}%</span>
            <button type="button" className={btn} aria-label="Zoom in" data-testid="source-zoom-in" disabled={zoomIdx === ZOOMS.length - 1} onClick={() => setZoomIdx((z) => z + 1)}><ZoomIn className="w-4 h-4" /></button>
            <button type="button" className={btn} aria-label="Fit to width" data-testid="source-zoom-fit" onClick={() => setZoomIdx(2)}><Maximize2 className="w-4 h-4" /></button>
            <button type="button" className={btn} aria-label="Rotate" data-testid="source-rotate" onClick={() => setRotate((r) => (r + 90) % 360)}><RotateCw className="w-4 h-4" /></button>
          </>
        )}
      </div>
      <div ref={boxRef} className="flex-1 overflow-auto bg-surface-sunken p-2 min-h-[320px]" data-testid="source-canvas-box">
        {src.loading && <p className="p-4 text-sm text-ink-muted" data-testid="source-loading">Loading document… large photos can take a few seconds.</p>}
        {src.error && <p className="p-4 text-sm text-danger" data-testid="source-error">{src.error}</p>}
        {src.kind === 'pdf' && <canvas ref={canvasRef} className="mx-auto block bg-white shadow" data-testid="source-pdf-canvas" />}
        {src.kind === 'image' && (
          <img src={src.url} alt="Uploaded invoice" data-testid="source-image"
            style={{ width: `${zoom * 100}%`, maxWidth: 'none', transform: rotate ? `rotate(${rotate}deg)` : undefined }}
            className="mx-auto block bg-white shadow" />
        )}
        {src.kind === 'table' && table && <SourceTable table={table} />}
        {src.kind === 'text' && <pre className="text-xs font-mono whitespace-pre-wrap text-ink p-2" data-testid="source-text">{text}</pre>}
        {src.kind === 'other' && <iframe title="Invoice source" src={src.url} className="w-full h-[60vh] bg-white" data-testid="source-frame" />}
      </div>
      {src.kind === 'pdf' && pages > 1 && (
        <div className="flex items-center justify-center gap-3 px-3 py-2 border-t border-surface-border">
          <button type="button" className={btn} aria-label="Previous page" data-testid="source-prev-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-ink" data-testid="source-page-label">Page {page} of {pages}</span>
          <button type="button" className={btn} aria-label="Next page" data-testid="source-next-page" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </section>
  );
}
